// Sessions du conseil (M16, lot L5-B) : liste avec état et quorum, création d'une session par le
// secrétariat ; navigation interne vers les élus et les règles. Un élu ne voit que ses séances.
import type { ResumeSession } from '@cc/contrats';
import { TYPES_SESSION } from '@cc/contrats';
import { Bouton, EtatVide, Lien, PuceEtat, Tableau, type ColonneTableau } from '@cc/ui';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { useSession as useSessionUtilisateur } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { useSessions } from './api';
import { FormulaireSession } from './FormulairesConseils';
import { libelleSession, texteQuorum, TON_SESSION } from './logique';
import { NavigationConseil } from './NavigationConseil';
import styles from '../achats/achats.module.css';

export const dateHeure = (iso: string) => new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Africa/Libreville' });

export function PageSessions() {
  const { contexte } = useSessionUtilisateur();
  const sessions = useSessions();
  const naviguer = useNavigate();
  const [creation, setCreation] = useState(false);
  const liste = sessions.data?.elements ?? [];
  const colonnes: ColonneTableau<ResumeSession>[] = [
    { id: 'numero', entete: 'Session', enTeteLigne: true, masquable: false, cellule: (s) => <Lien href={`/conseil/sessions/${s.id}`} className="cc-mono">{s.numero}</Lien>, valeurTri: (s) => s.numero },
    { id: 'intitule', entete: 'Intitulé', cellule: (s) => `${s.intitule}${s.fictif ? ' (fictif)' : ''}` },
    { id: 'type', entete: 'Type', cellule: (s) => TYPES_SESSION[s.type] },
    { id: 'date', entete: 'Séance', cellule: (s) => dateHeure(s.date_seance), valeurTri: (s) => s.date_seance },
    { id: 'regle', entete: 'Règle', cellule: (s) => (s.regle ? `${s.regle.libelle} (v${s.regle.version})` : 'Aucune règle : session préparatoire') },
    { id: 'quorum', entete: 'Quorum', cellule: (s) => texteQuorum(s.quorum) },
    { id: 'etat', entete: 'État', cellule: (s) => <PuceEtat ton={TON_SESSION[s.etat]} libelle={libelleSession(s.etat)} /> },
  ];
  return (
    <>
      <EnTetePage
        etiquette="Conseil"
        titre="Sessions du conseil"
        description="Convocation, présences, quorum calculé selon la règle validée de la collectivité, délibérations, procès-verbal, transmission et publication."
        actions={peut(contexte, 'assemblee', 'creer') ? <Bouton variante="principal" icone={Plus} onClick={() => setCreation(true)}>Nouvelle session</Bouton> : null}
      />
      <NavigationConseil actif="sessions" />
      <EtatRequete chargement={sessions.isPending} erreur={sessions.error} onReessayer={() => void sessions.refetch()} lot="L5-B" fonction="Les sessions du conseil">
        <section className={styles.bloc} aria-labelledby="titre-sessions">
          <h2 id="titre-sessions">Sessions</h2>
          {liste.length === 0 ? <EtatVide titre="Aucune session"><p>Aucune session du conseil n&apos;est enregistrée ou visible avec vos droits.</p></EtatVide> : <Tableau legende="Sessions du conseil" colonnes={colonnes} lignes={liste} cleLigne={(s) => s.id} cleColonnes="conseil-sessions" />}
        </section>
      </EtatRequete>
      {creation ? <FormulaireSession onFermer={() => setCreation(false)} onCree={(id) => { setCreation(false); void naviguer(`/conseil/sessions/${id}`); }} /> : null}
    </>
  );
}
